import { useState } from 'react'
import { useMutation as useConnectMutation } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@magnetic/notification'
import {
  createProject,
  validateRepository,
  deleteProject,
} from '@/lib/api'
import { invalidateProjectList } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import type { CreateProjectResponse } from '@/gen/catpocs/v1/projects_pb'
import type { ValidateRepositoryResponse } from '@/gen/catpocs/v1/admin_pb'

type DeletableProject = {
  id: string
  name?: string
  displayName?: string
}

interface UseProjectActionsOptions {
  onProjectCreated?: (projectId?: string) => void
  onCreateCompleted?: () => void
}

export function useProjectActions(options?: UseProjectActionsOptions) {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const [isDeletingProjects, setIsDeletingProjects] = useState(false)

  const createProjectMutation = useMutationWithToast<CreateProjectResponse>(createProject, {
    successTitle: 'Project created',
    successMessage: 'Your project has been created successfully.',
    errorTitle: 'Failed to create project',
    invalidate: invalidateProjectList,
    onSuccess: (data) => {
      options?.onCreateCompleted?.()
      options?.onProjectCreated?.(data.project?.id)
    },
  })

  const validateRepositoryMutation = useMutationWithToast<ValidateRepositoryResponse>(validateRepository, {
    successTitle: 'Repository validated',
    showSuccessToast: false,
    errorTitle: 'Repository validation failed',
  })

  const deleteProjectMutation = useConnectMutation(deleteProject)

  const deleteProjects = async (projects: DeletableProject[]) => {
    if (projects.length === 0) {
      return
    }

    setIsDeletingProjects(true)
    const errors: string[] = []

    try {
      for (const project of projects) {
        try {
          await deleteProjectMutation.mutateAsync({ id: project.id })
        } catch (error) {
          errors.push(`${project.displayName || project.name || project.id}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      }

      await invalidateProjectList(queryClient)

      if (errors.length === 0) {
        addToast({
          status: 'positive',
          title: 'Projects deleted',
          children: `Successfully deleted ${projects.length} project${projects.length > 1 ? 's' : ''}`,
        })
      } else {
        addToast({
          status: 'warning',
          title: 'Some deletions failed',
          children: errors.join('; '),
        })
      }
    } finally {
      setIsDeletingProjects(false)
    }
  }

  return {
    createProjectMutation,
    validateRepositoryMutation,
    deleteProjects,
    isDeletingProjects,
  }
}
